import { useSelector } from 'react-redux';

import { Filter } from 'app/modules/filters/models';
import { FILTER_OPERATOR } from 'app/modules/filters/constants';
import {
  CTR_FILING_FILTER_KEYS,
  FILTERS_LOCAL_STORAGE_KEYS,
} from 'app/modules/fincenCtr/filters';
import { FincenCtrTablePages } from 'app/modules/fincenCtr/models';
import { selectAgent } from 'app/modules/session/selectors';
import { FE_BE_CTR_STATUS_MAPPING } from 'app/modules/fincenCtr/status';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';

/**
 * Page-identity scoping that always applies regardless of user input.
 * Not shown as chips — enforced server-side via the request payload.
 *
 * - My Filings → reviewer = current agent
 * - Queued Filings → no scope here; queue narrowing is enforced by the BE via
 *   the `my_queues_only` request flag (see CtrFilingsTable).
 * - Admin / Queue Details → no scope
 */
export const useCtrPageScopeFilters = (
  pageType: FincenCtrTablePages,
): Filter[] => {
  const sessionAgent = useSelector(selectAgent);
  switch (pageType) {
    case FincenCtrTablePages.MY_FILINGS:
      return [
        {
          key: CTR_FILING_FILTER_KEYS.REVIEWER,
          operator: FILTER_OPERATOR.IS_ONE_OF,
          value: [sessionAgent.id],
        },
      ];
    default:
      return [];
  }
};

/**
 * Full filter set for the CTR list request: user filters + page scope.
 * Scope is appended so it ANDs with user input, not overrides it.
 */
export const useCtrPageFilters = (pageType: FincenCtrTablePages): Filter[] => {
  // Same localStorage key the filter bar writes to (CtrsTab); the hook syncs
  // across instances in-tab, so the payload tracks the user's filters live.
  const [userFilters] = useLocalStorageState<Filter[]>(
    FILTERS_LOCAL_STORAGE_KEYS[pageType],
    [],
  );
  const scopeFilters = useCtrPageScopeFilters(pageType);
  return [...userFilters, ...scopeFilters];
};

export const createCtrFilterPayload = (filters: Filter[]): Filter[] =>
  filters.flatMap((filter): Filter[] => {
    const { key, operator, value } = filter;
    switch (key) {
      case CTR_FILING_FILTER_KEYS.ID: {
        // FE TEXT input (comma-separated IDs) → BE IN with int[]
        if (operator === FILTER_OPERATOR.CONTAINS_TEXT) {
          const ids = String(value)
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !Number.isNaN(n) && n > 0);
          if (ids.length === 0) {
            return [];
          }
          return [{ key, operator: FILTER_OPERATOR.IS_ONE_OF, value: ids }];
        }
        return [filter];
      }
      case CTR_FILING_FILTER_KEYS.STATUS: {
        // The user picks status buckets (e.g. "Submission failed"); expand
        // each bucket to the underlying raw CtrFilingStatus values that the
        // BE actually stores on CtrFiling.status.
        if (operator === FILTER_OPERATOR.IS_ONE_OF) {
          const expanded = (
            value as Array<keyof typeof FE_BE_CTR_STATUS_MAPPING>
          ).flatMap((bucket) => FE_BE_CTR_STATUS_MAPPING[bucket] ?? []);
          if (expanded.length === 0) {
            return [];
          }
          return [{ key, operator, value: expanded }];
        }
        return [filter];
      }
      default:
        return [filter];
    }
  });
